import { EChartOption } from 'echarts';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import moment from 'moment';
import { timelineDataSelector } from '@store/timeline/data/selectors';
import { humanize } from 'utils';
import { UNITS } from '@components/session-timeline/utils/constants';
import formatBPS from '../utils/formatBPS';

const useLinerOption = (eventKey: string, dateRange: number[]): EChartOption => {
  const timelineData = useSelector(timelineDataSelector);
  const min = dateRange[0];
  const max = dateRange[dateRange.length - 1];
  const diff = moment(max).diff(min, 'm');
  const linerDataMap = get(timelineData, [eventKey], []).reduce((result, {
    startTimeMs,
    endTimeMs,
    startValue,
    endValue,
  }) => {
    const r = { ...result };
    const start = Math.max(min, startTimeMs);
    const end = Math.min(max, endTimeMs);
    if (start > end) {
      return r;
    }
    r[start] = startValue;
    r[end] = endValue;
    return r;
  }, {});
  const linerData = Object.keys(linerDataMap).reduce((result: Array<[number, string]>, time) => {
    result.push([parseFloat(time), linerDataMap[time]]);
    return result;
  }, []);
  const dashed = linerData.length > 0 ? [
    {
      type: 'line',
      data: [
        [min, linerData[0][1]],
        [linerData[0][0], linerData[0][1]],
      ],
      lineStyle: {
        width: 1,
        type: 'dashed',
      },
      symbol: 'none',
      itemStyle: {
        color: '#A9A9A9',
      },
    },
    {
      type: 'line',
      data: [
        [linerData[linerData.length - 1][0], linerData[linerData.length - 1][1]],
        [max, linerData[linerData.length - 1][1]],
      ],
      lineStyle: {
        width: 1,
        type: 'dashed',
      },
      symbol: 'none',
      itemStyle: {
        color: '#A9A9A9',
      },
    },
  ] : [];
  return {
    tooltip: {
      formatter: (params) => {
        let row = get(params, [0, 'value'], null);
        if (!row) {
          row = get(params, ['data'], ['N/A', 'N/A']);
        }
        const [time] = row;
        let value = row[1];
        let unit = UNITS[eventKey];
        if (unit === 'bps') {
          [value, unit] = formatBPS(value, eventKey);
        } else {
          value = humanize(value, '');
        }
        return `
          <div><strong>${moment(time).format('MMM DD, YYYY HH:mm:ss')}</strong></div>
          <div>${value} ${unit}</div>
        `;
      },
    },
    grid: {
      show: true,
      left: 30,
      right: 95,
      top: 10,
      bottom: 10,
      borderWidth: 0,
    },
    yAxis: {
      type: 'value',
      show: false,
    },
    xAxis: {
      type: 'time',
      show: false,
      data: dateRange,
      min,
      max,
      axisPointer: {
        show: true,
        type: 'none',
        label: {
          show: false,
        },
      },
      splitNumber: Math.min(14, diff),
    },
    series: [
      {
        type: 'line',
        step: 'end',
        data: linerData.length === 1 ? [
          [min, linerData[0][1]],
          ...linerData,
        ] : linerData,
        symbolSize: 1,
        lineStyle: {
          width: 1,
        },
        itemStyle: {
          color: '#A9A9A9',
        },
      },
      ...dashed,
    ],
  };
};

export default useLinerOption;
